"use client"

import { datadogRum } from "@datadog/browser-rum"
import { ReferralData } from "@nelo/signup-lib"
import { sendGTMEvent } from "@next/third-parties/google"
import { useRouter, useSearchParams } from "next/navigation"
import React, { useEffect, useMemo, useState } from "react"

import { IDVerificationStatus, LoanApplicationState } from "@lib/clients/nelo-api-client"
import { PSEUDO_ID_COOKIE } from "@lib/constants"
import { setSessionAuthToken, useAuthToken } from "@lib/hooks/use-auth-token"
import { getSessionCookie } from "@lib/util/get-session-cookie-client"
import { retrieveUTMParams } from "@lib/util/utm-params"
import SideSheet from "@modules/common/components/side-sheet"
import { sendGoogleAnalyticsEvent } from "@modules/common/utils/analytics"
import SignupFlow from "@modules/signup-and-order-flow/components/signup/SignupFlow"
import { CheckoutExperimentsDefinition } from "@modules/signup-and-order-flow/experiments/experiment-interfaces"
import { useClientInitialization } from "@modules/signup-and-order-flow/util/use-client-initializations"
import PaymentOptionsPicker from "modules/signup-and-order-flow/components/payment-options-picker"
import { RegionInfo } from "types/global"

import ApprovedInsufficientFundsScreen from "./approved-insuficient-funds"
import LoadingScreen from "./loading"
import LoanOptions from "./loan-options"
import RejectedScreen from "./rejected"
import { WhatsappIdentityVerificationType } from "../index"
import ServerErrorScreen from "./server-error"
import SuccessScreen from "./success"
import VerificationStack from "./verification-stack"
import { useCheckIdentityVerification } from "../util/use-check-identity-status"
import { useLoanApplicationStatus } from "../util/use-check-loan-application"
import { useCheckSufficientFunds } from "../util/use-check-sufficient-finds"
import useProductData from "../util/use-fetch-product-data"
import { useGetCalculatedPrices } from "../util/use-get-product-details"

const statusesEligibleToStartVerification = new Set([null, "UNVERIFIED", "REJECTED", "DOCUMENT_MISMATCH_STATUS"])

enum ChildStep {
  Signup = "Signup",
  Loading = "Loading",
  Success = "Success",
  Rejected = "Rejected",
  LoanOptions = "Loan Options",
  IdentityVerificationStack = "Identity Verification Stack",
  ServerError = "Server Error",
  InsufficientFunds = "Insufficient Funds",
  SignupNative = "Signup Native",
  PaymentOptionsPicker = "Payment Options Picker",
}

export interface Props {
  treatments: CheckoutExperimentsDefinition
}

export function SignupAndOrderFlow({ treatments }: Props) {
  const whatsappIdentityVerification = treatments.whatsapp_identity_verification.config as WhatsappIdentityVerificationType

  const router = useRouter()
  const searchParams = useSearchParams()
  const isSheetOpen = searchParams.get("signup") === "1"
  const authToken = useAuthToken()
  const { isInitialized } = useClientInitialization()
  const loanApplicationStatus = useLoanApplicationStatus(isInitialized, authToken !== null)
  const [recentlySubmittedVerification, setRecentlySubmittedVerification] = useState<boolean>(false)
  const idVerificationStatus = useCheckIdentityVerification(isInitialized, authToken !== null)
  const [optimisticVerificationStatus, setOptimisticVerificationStatus] = useState<IDVerificationStatus | null>(null)
  const [hasOrderBeenPlaced, setHasOrderBeenPlaced] = useState<boolean>(false)
  const [serverErrorOccurred, setServerErrorOccurred] = useState<boolean>(false)
  const [cartTotal, setCartTotal] = useState<number | null>(null)
  const { isSufficientFunds, creditLimit } = useCheckSufficientFunds(loanApplicationStatus?.status || null, cartTotal, hasOrderBeenPlaced)
  const productId = searchParams.get("productId")
  const variantId = searchParams.get("variantId")
  const [preSelectedInstallment, setPreSelectedInstallment] = useState<number | undefined>()

  const { product, discounts, region, selectedVariant } = useProductData(productId)
  const calculatedPrices = useGetCalculatedPrices(product, selectedVariant, region as RegionInfo, discounts)

  useMemo(() => {
    datadogRum.setGlobalContextProperty("signup_flow", "web_native")
  }, [])

  useEffect(() => {
    if (idVerificationStatus !== null) {
      setOptimisticVerificationStatus(idVerificationStatus)
      setRecentlySubmittedVerification(false)
    }
  }, [idVerificationStatus])

  useEffect(() => {
    if (recentlySubmittedVerification) {
      setOptimisticVerificationStatus(IDVerificationStatus.PROCESSING)
    }
  }, [recentlySubmittedVerification])

  useEffect(() => {
    sendGoogleAnalyticsEvent("XP_INCLUSION", {
      featureName: "mktp_checkout_flow_v2",
      trackingKey: "mktp_checkout_flow_v2_experiment",
      treatmentName: treatments.mktp_checkout_flow_v2.treatment,
    })
  }, [treatments.mktp_checkout_flow_v2.treatment])

  const handleSheetClose = () => {
    if (typeof window === "undefined") return
    const currentPath = window.location.pathname
    const currentSearch = window.location.search
    const newSearch = new URLSearchParams(currentSearch)
    newSearch.delete("signup")
    const newPath = `${currentPath}${newSearch.toString() ? `?${newSearch.toString()}` : ""}`
    router.push(newPath)
  }

  const handleBackToOptions = () => {
    if (childStep === ChildStep.SignupNative) {
      setChildStep(ChildStep.PaymentOptionsPicker)
    } else {
      handleSheetClose()
    }
  }

  const [childStep, setChildStep] = useState<ChildStep>(ChildStep.Signup)

  useEffect(() => {
    if (!authToken) {
      setChildStep(ChildStep.PaymentOptionsPicker)
    } else if (serverErrorOccurred) {
      setChildStep(ChildStep.ServerError)
    } else if (
      isSufficientFunds === false &&
      hasOrderBeenPlaced &&
      optimisticVerificationStatus === IDVerificationStatus.VERIFIED &&
      loanApplicationStatus?.status === LoanApplicationState.APPROVED
    ) {
      setChildStep(ChildStep.InsufficientFunds)
    } else if (
      isSufficientFunds === true &&
      hasOrderBeenPlaced &&
      optimisticVerificationStatus === IDVerificationStatus.VERIFIED &&
      loanApplicationStatus?.status === LoanApplicationState.APPROVED
    ) {
      setChildStep(ChildStep.Success)
    } else if (loanApplicationStatus?.status === LoanApplicationState.REJECTED) {
      setChildStep(ChildStep.Rejected)
    } else if (!hasOrderBeenPlaced) {
      setChildStep(ChildStep.LoanOptions)
    } else if (statusesEligibleToStartVerification.has(optimisticVerificationStatus)) {
      setChildStep(ChildStep.IdentityVerificationStack)
    } else {
      setChildStep(ChildStep.Loading)
    }
  }, [authToken, hasOrderBeenPlaced, optimisticVerificationStatus, loanApplicationStatus, serverErrorOccurred, isSufficientFunds])

  useEffect(() => {
    if (isSheetOpen) {
      sendGTMEvent({
        event: "signup_and_order_flow_step",
        childStep: childStep,
      })
      sendGoogleAnalyticsEvent("SIGNUP_AND_ORDER_FLOW_STEP", {
        step: childStep,
        checkoutType: "web_native",
      })
    }
  }, [childStep, isSheetOpen])

  const optimisticallyUpdateVerificationStatus = () => {
    setRecentlySubmittedVerification(true)
  }

  const handleNativeWebSignupSuccess = (authToken: string, userUuid: string) => {
    setSessionAuthToken(authToken)
    sendGTMEvent({
      event: "signup_success",
      userUuid,
      productId,
      variantId,
    })

    sendGoogleAnalyticsEvent("SIGNUP_SUCCESS", {
      flow_type: "web_native",
    })
    datadogRum.addAction(`signup.success`)

    if (!productId) {
      if (typeof window !== "undefined") {
        window.location.href = `${process.env.NEXT_PUBLIC_SIGNUP_ORIGIN}/signup/completed`
      }
      return
    }
  }

  const onOrderComplete = () => {
    sendGoogleAnalyticsEvent("PURCHASE", {
      value: cartTotal,
      checkoutType: "web_native",
    })
    datadogRum.addAction(`order.purchase`)
    setHasOrderBeenPlaced(true)
  }

  const onServerError = () => {
    setServerErrorOccurred(true)
  }

  const onCartTotalChange = (total: number) => {
    setCartTotal(total)
  }

  const initialReferralData = useMemo((): ReferralData => {
    const utmParams = retrieveUTMParams()
    const pseudoId = getSessionCookie(PSEUDO_ID_COOKIE)
    const url = typeof window !== "undefined" ? window.location.href.toString() : undefined
    return {
      source: utmParams?.utm_source,
      medium: utmParams?.utm_medium,
      campaign: utmParams?.utm_campaign,
      term: utmParams?.utm_term,
      ad: utmParams?.utm_ad,
      content: utmParams?.utm_content,
      utmId: utmParams?.utm_id,
      url: url,
      productId: productId || undefined,
      variantId: selectedVariant?.id,
      isMarketplace: true,
      gclid: utmParams?.utm_gclid,
      marketplaceCheckoutType: "web_native",
      productHandle: product?.handle || undefined,
      storefrontSessionId: pseudoId || undefined,
    }
  }, [productId, selectedVariant, product])

  const renderChildStep = () => {
    switch (childStep) {
      case ChildStep.PaymentOptionsPicker:
        return (
          <PaymentOptionsPicker
            calculatedPrices={calculatedPrices}
            onInstallmentSelected={setPreSelectedInstallment}
            onBack={handleSheetClose}
            onContinue={() => {
              setChildStep(ChildStep.SignupNative)
            }}
            preSelectedInstallment={preSelectedInstallment}
          />
        )
      case ChildStep.SignupNative:
        return (
          <SignupFlow
            treatments={treatments}
            onSignupSuccess={handleNativeWebSignupSuccess}
            onClose={handleBackToOptions}
            flowType="web_native"
            initialReferralData={initialReferralData}
          />
        )
      case ChildStep.Loading:
        return <LoadingScreen />
      case ChildStep.Success:
        return <SuccessScreen />
      case ChildStep.Rejected:
        return <RejectedScreen />
      case ChildStep.LoanOptions:
        return (
          <LoanOptions
            variantId={variantId}
            onComplete={onOrderComplete}
            onServerError={onServerError}
            onCartTotalChange={onCartTotalChange}
            preSelectedInstallment={preSelectedInstallment}
          />
        )
      case ChildStep.InsufficientFunds:
        return <ApprovedInsufficientFundsScreen creditLimit={creditLimit} cartTotal={cartTotal} />
      case ChildStep.ServerError:
        return <ServerErrorScreen />
      case ChildStep.IdentityVerificationStack:
        return (
          <VerificationStack
            treatments={treatments}
            authToken={authToken!}
            onComplete={optimisticallyUpdateVerificationStatus}
            webVerificationPath="web_native"
            productId={productId}
            handleCloseSheet={handleSheetClose}
            whatsappIdentityVerification={whatsappIdentityVerification}
          />
        )
      default:
        return (
          <LoadingScreen
            title="Estamos revisando tu aplicación"
            subtitle="Esto puede tardar unos minutos. Por favor no actualices la página."
          />
        )
    }
  }

  return (
    <SideSheet isOpen={isSheetOpen} close={handleSheetClose}>
      <div className="h-full w-full">{renderChildStep()}</div>
    </SideSheet>
  )
}
